import React, { useState, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import ItemView from './ItemView';
import { AuthContext } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { fetchItemUse, withAuthCheck, AuthError, fetchItemGDeposit } from '../services/api';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

export default function ItemViewDetails({ open, onClose, item }) {
    const token = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const [isProcessing, setIsProcessing] = useState(false);
    const [depositDialogOpen, setDepositDialogOpen] = useState(false);
    const [depositQty, setDepositQty] = useState(1);
    const [depositProcessing, setDepositProcessing] = useState(false);

    const handleUseClick = () => {
        if (!item || !item.code) return;
        setIsProcessing(true);
        withAuthCheck(fetchItemUse, token, item.code)
            .then(data => {
                onClose(); // Закрываем диалог
                setIsProcessing(false);
                showSnackbar(data.result, "success");
            })
            .catch(err => {
                onClose(); // Закрываем диалог
                setIsProcessing(false);
                if (err instanceof AuthError) {
                    // WebAuthForm покажется автоматически через AuthProvider
                    return;
                }
                const errorMessage = err.result || err.message || "error";
                showSnackbar(errorMessage, "error");
            });
    };

    const handleDepositClick = () => {
        setDepositQty(1);
        setDepositDialogOpen(true);
    };

    const handleDepositSubmit = () => {
        if (!item || !item.code || !depositQty || depositQty <= 0) return;
        setDepositProcessing(true);
        withAuthCheck(fetchItemGDeposit, token, item.code, Number(depositQty))
            .then(data => {
                setDepositProcessing(false);
                setDepositDialogOpen(false);
                onClose();
                showSnackbar(data.result || 'Deposited', 'success');
            })
            .catch(err => {
                setDepositProcessing(false);
                setDepositDialogOpen(false);
                onClose();
                if (err instanceof AuthError) return;
                const errorMessage = err.result || err.message || 'error';
                showSnackbar(errorMessage, 'error');
            });
    };

    // Проверяем, что массив actions существует и не пуст, и содержит "use"
    const hasUseAction =
        Array.isArray(item.actions) && item.actions.length > 0 && item.actions.includes("use");
    // Проверяем, что есть gdeposit
    const hasGDepositAction =
        Array.isArray(item.actions) && item.actions.length > 0 && item.actions.includes("gdeposit");

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    {item.name} ({item.qty})
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {item.code ? (
                        <ItemView itemCode={item.code} />
                    ) : (
                        <Typography color="text.secondary">Error</Typography>
                    )}
                </DialogContent>
                {(hasUseAction || hasGDepositAction) && (
                    <DialogActions sx={{ justifyContent: 'flex-start', px: 3, mb:2 }}>
                        {hasUseAction && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleUseClick}
                                disabled={isProcessing}
                            >
                                Use
                            </Button>
                        )}
                        {hasGDepositAction && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleDepositClick}
                                disabled={isProcessing}
                                sx={{ ml: hasUseAction ? 1 : 0 }}
                            >
                                Deposit to Guild
                            </Button>
                        )}
                    </DialogActions>
                )}
            </Dialog>
            {/* Deposit Dialog */}
            <Dialog open={depositDialogOpen} onClose={() => setDepositDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Deposit to guild</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        <b> {item.name} </b> × <b>{depositQty}</b>
                    </Typography>
                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        margin="normal"
                        value={depositQty}
                        onChange={e => setDepositQty(e.target.value)}
                        error={depositQty === '' || Number(depositQty) <= 0 || Number(depositQty) > item.qty}
                        helperText={
                            depositQty === '' || Number(depositQty) <= 0
                                ? 'Enter a quantity greater than 0'
                                : Number(depositQty) > item.qty
                                    ? `Maximum: ${item.qty}`
                                    : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {item.code}
                                </InputAdornment>
                            )
                        }}
                        disabled={depositProcessing}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDepositSubmit}
                        disabled={
                            depositProcessing ||
                            depositQty === '' ||
                            Number(depositQty) <= 0 ||
                            Number(depositQty) > item.qty
                        }
                        color="primary"
                        variant="contained"
                    >
                        Yes
                    </Button>
                    <Button onClick={() => setDepositDialogOpen(false)} disabled={depositProcessing}>No</Button>
                </DialogActions>
            </Dialog>
            <Backdrop
                open={isProcessing || depositProcessing}
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}